/**
 * Header Component - Reusable header for all pages
 * Supports multiple variants: landing, authenticated, public, minimal
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = '/assets/images/logo.png';
const TITLE_CARD_URL = '/assets/images/title_card.png';

/**
 * Header variants:
 * - 'landing': Full nav with Features, Pricing, FAQ, Sign In + mobile menu
 * - 'authenticated': User nav with Tests, Practice, user info, logout
 * - 'public': Nav links that redirect to landing page sections
 * - 'minimal': Just logo, no navigation (for test interfaces)
 * - 'test': Minimal header with custom action button (for quiz/test pages)
 */
export default function Header({ 
  variant = 'minimal',
  user = null,
  setUser = null,
  onSignIn = null,
  onShowUpgrade = null,
  logoClickPath = '/',
  activePage = null, // 'tests' or 'practice' for authenticated nav underline
  onLogoClick = null, // Custom logo click handler (for exit dialogs in test mode)
  rightContent = null // Custom right-side content (for exit buttons in test mode)
}) {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    if (variant === 'landing') {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    if (setUser) setUser(null);
    navigate('/');
  };

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else if (variant === 'landing') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(logoClickPath);
    }
  };

  const handlePracticeClick = () => {
    if (user?.subscription_tier === 'basic' && onShowUpgrade) {
      onShowUpgrade();
    } else {
      navigate('/practice');
    }
    setMobileMenuOpen(false);
  };

  // Base header styles
  const headerStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#91ccff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '0 2rem'
  };

  const logoContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    cursor: 'pointer',
    flex: '0 1 auto',
    minWidth: 0
  };

  const navButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: 'white',
    fontWeight: '500'
  };

  const mobileMenuButtonStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'white',
    padding: '0.5rem'
  };

  // Render navigation based on variant
  const renderNavigation = () => {
    switch (variant) {
      case 'landing':
      case 'public':
        return (
          <>
            {/* Desktop Navigation */}
            <div style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="desktop-nav">
              <button onClick={() => scrollToSection('features')} style={navButtonStyle}>Features</button>
              <button onClick={() => scrollToSection('pricing')} style={navButtonStyle}>Pricing</button>
              <button onClick={() => scrollToSection('faq')} style={navButtonStyle}>FAQ</button>
              {variant === 'landing' && onSignIn ? (
                <button onClick={onSignIn} data-testid="header-sign-in-btn" style={navButtonStyle}>Sign In</button>
              ) : (
                <button onClick={() => navigate('/?showAuth=true')} style={navButtonStyle}>Sign In</button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={mobileMenuButtonStyle}
              className="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </>
        );

      case 'authenticated':
        return (
          <>
            {/* Desktop Navigation */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }} className="desktop-nav">
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                <button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  style={{ 
                    ...navButtonStyle, 
                    textDecoration: activePage === 'dashboard' ? 'underline' : 'none' 
                  }}
                >
                  Dashboard
                </button>
                <button
                  onClick={handlePracticeClick}
                  style={{ 
                    ...navButtonStyle,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.15rem',
                    textDecoration: activePage === 'practice' ? 'underline' : 'none'
                  }}
                >
                  Practice
                </button>
                <button
                  onClick={() => { navigate('/tests'); setMobileMenuOpen(false); }}
                  style={{ 
                    ...navButtonStyle, 
                    textDecoration: activePage === 'tests' ? 'underline' : 'none' 
                  }}
                >
                  Full-Length Tests
                </button>
                <Button 
                  onClick={handleLogout} 
                  variant="ghost" 
                  style={{ color: 'white', padding: '0.5rem' }}
                  data-testid="logout-btn"
                >
                  <LogOut size={20} />
                </Button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={mobileMenuButtonStyle}
              className="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </>
        );

      case 'minimal':
      default:
        // Support custom right content for test/quiz pages
        return rightContent || null;
    }
  };

  // Render mobile menu
  const renderMobileMenu = () => {
    if (!mobileMenuOpen || variant === 'minimal') return null;

    const mobileMenuStyle = {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      background: '#91ccff',
      padding: '1rem 2rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
    };

    const mobileNavButtonStyle = {
      ...navButtonStyle,
      padding: '0.75rem',
      textAlign: 'left'
    };

    if (variant === 'landing' || variant === 'public') {
      return (
        <div style={mobileMenuStyle} className="mobile-menu">
          <button onClick={() => scrollToSection('features')} style={mobileNavButtonStyle}>Features</button>
          <button onClick={() => scrollToSection('pricing')} style={mobileNavButtonStyle}>Pricing</button>
          <button onClick={() => scrollToSection('faq')} style={mobileNavButtonStyle}>FAQ</button>
          {variant === 'landing' && onSignIn ? (
            <button onClick={() => { onSignIn(); setMobileMenuOpen(false); }} style={mobileNavButtonStyle}>Sign In</button>
          ) : (
            <button onClick={() => { navigate('/?showAuth=true'); setMobileMenuOpen(false); }} style={mobileNavButtonStyle}>Sign In</button>
          )}
        </div>
      );
    }

    if (variant === 'authenticated') {
      return (
        <div style={mobileMenuStyle} className="mobile-menu">
          <button 
            onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} 
            style={mobileNavButtonStyle}
          >
            Dashboard
          </button>
          <button 
            onClick={() => { handlePracticeClick(); setMobileMenuOpen(false); }} 
            style={{ 
              ...mobileNavButtonStyle,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Practice
          </button>
          <button 
            onClick={() => { navigate('/tests'); setMobileMenuOpen(false); }} 
            style={mobileNavButtonStyle}
          >
            Full-Length Tests
          </button>
          <button 
            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
            style={{ 
              ...mobileNavButtonStyle,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <header style={headerStyle}>
      <div style={containerStyle}>
        <div style={logoContainerStyle} onClick={handleLogoClick}>
          <img
            src={LOGO_URL}
            alt="Practice4SAT Logo"
            style={{ height: '70px', width: 'auto', flexShrink: 0 }}
          />
          <img
            src={TITLE_CARD_URL}
            alt="Practice4SAT"
            style={{ height: '50px', width: 'auto', flexShrink: 0 }}
          />
        </div>

        {renderNavigation()}
      </div>

      {renderMobileMenu()}
    </header>
  );
}
